import { RouterProvider } from "@tanstack/react-router"
import PropTypes from "prop-types"
import React from "react"
import { HotkeysProvider } from "react-hotkeys-hook"

import { ChartSettingsProvider } from "#Contexts/ChartSettingsContext"
import { EnabledLabFeaturesContext } from "#Contexts/EnabledLabFeaturesContext"
import { FormatterContext } from "#Contexts/FormatterContext"
import { RestClientContext } from "#Contexts/RestClientContext"
import { PlatformContext } from "#React/hooks/usePlatformFeature"
import { tanstackRouter } from "#React/router/routeTree"
import { AlertDialogProvider } from "#React/ui/AlertDialog"
import { ChartExportProvider } from "#React/ui/ASChart/contexts/ChartExportContext"
import { ToastProvider } from "#React/ui/Toast"
import { ReactQueryProvider } from "#Utils/react-query-client"
import RestApiClient from "#Utils/RestApiClient"

import { FullscreenChartsProvider } from "./FullscreenCharts/contexts/fullscreenChartsContext"

// Application shell: the page-agnostic data providers that every route needs,
// wrapping `<RouterProvider/>`. TanStack owns the render tree from there —
// `rootRoute.component` (`RootLayout`) renders the route-aware chrome and the
// `<Outlet/>`. The route-aware pieces (navigation, overlays, error boundary,
// menu) live in `RootLayout`, not here, because they need the active route.
const App = ({
  formatters,
  enabledLabFeatures,
  platforms,
  chartSettings,
  restClientSettings,
}) => {
  const restApiClient = React.useMemo(() => {
    return new RestApiClient(restClientSettings.endpoint, restClientSettings.token)
  }, [restClientSettings.endpoint, restClientSettings.token])

  return (
    <HotkeysProvider initiallyActiveScopes={["global"]}>
      <EnabledLabFeaturesContext.Provider value={enabledLabFeatures}>
        <PlatformContext.Provider value={platforms}>
          <ReactQueryProvider>
            <RestClientContext.Provider value={restApiClient}>
              <ChartSettingsProvider value={chartSettings}>
                <FormatterContext.Provider value={formatters}>
                  <ChartExportProvider>
                    <FullscreenChartsProvider>
                      <ToastProvider>
                        <AlertDialogProvider>
                          <RouterProvider router={tanstackRouter} />
                        </AlertDialogProvider>
                      </ToastProvider>
                    </FullscreenChartsProvider>
                  </ChartExportProvider>
                </FormatterContext.Provider>
              </ChartSettingsProvider>
            </RestClientContext.Provider>
          </ReactQueryProvider>
        </PlatformContext.Provider>
      </EnabledLabFeaturesContext.Provider>
    </HotkeysProvider>
  )
}

App.propTypes = {
  formatters: PropTypes.object,
  enabledLabFeatures: PropTypes.array,
  platforms: PropTypes.array,
  chartSettings: PropTypes.shape({
    showMarkers: PropTypes.bool,
    horizontalSupportLines: PropTypes.bool,
    verticalSupportLines: PropTypes.bool,
    colorPalette: PropTypes.string,
    lineThickness: PropTypes.number,
  }),
  restClientSettings: PropTypes.shape({
    endpoint: PropTypes.string,
    token: PropTypes.string,
  }),
}

export default App
